import DialogTextField from '@Components/General/dialog-content/text-field';
import InfiniteScroll from '@Components/General/InfiniteScroll';
import QuickOptions, { QuickOption } from '@Components/General/QuickOptions';
import GridSkeleton from '@Components/General/Skeletons/grid';
import useDialog from '@hooks/useDialog';
import {
    ContentCopyRounded,
    DashboardRounded,
    DeleteOutlineRounded,
    DriveFileMoveOutlined,
    DriveFileRenameOutlineRounded,
    MailOutlineRounded,
} from '@mui/icons-material';
import { Avatar, Box, Card, CardHeader } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { maxToneToLevel } from '@utils/colorUtils';
import { closeOptions } from '@utils/utils';
import { forwardRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import TemplateSelect from '../template-select';

const VariantQuickActions = ({ template, onSelect, setSelectedTemplate }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dialog = useDialog();
    const { control } = useForm({});
    const queryClient = useQueryClient();
    const [templateSelectOpen, setTemplateSelectOpen] = useState(false);

    const invalidateVariants = () =>
        queryClient.invalidateQueries({
            queryKey: ['emailTemplateVariants'],
        });

    const invalidateTemplates = () =>
        queryClient.invalidateQueries({ queryKey: ['emailTemplates'] });

    const updateTemplate = useMutation({
        mutationKey: ['updateEmailTemplate'],
        onSuccess: () => {
            invalidateVariants();
            dialog.close();
            closeOptions();
        },
    });

    const { mutate: handleDuplicate } = useMutation({
        mutationKey: ['duplicateEmailTemplateVariant'],
        onSuccess: res => {
            navigate({ to: res.id });
            invalidateVariants();
        },
    });

    const deleteMutation = useMutation({
        mutationKey: ['deleteEmailTemplateVariant'],
        onSuccess: data => {
            if (template.is_default) setSelectedTemplate(data?.new_default);
            invalidateTemplates();
            invalidateVariants();
            dialog.close();
        },
    });

    const { mutate: createEmailDraft } = useMutation({
        mutationKey: ['createEmailDraft'],
        onSuccess: res => {
            navigate({ to: `../drafts/${res.id}/edit` });
        },
    });

    const quickOptions: QuickOption[] = [
        {
            label: t('Rename'),
            onClick: () => {
                dialog?.open({
                    title: t('Rename Variant'),
                    confirmLabel: t('Rename'),
                    content: (
                        <DialogTextField
                            name="title"
                            control={control}
                            defaultValue={template.title}
                            label={t('New Title')}
                        />
                    ),
                    onConfirm: () => [template.id, control._formValues],
                    confirmMutation: updateTemplate,
                });
            },
            Icon: DriveFileRenameOutlineRounded,
        },
        {
            label: t('Make a Copy'),
            onClick: () => handleDuplicate(template.id),
            Icon: ContentCopyRounded,
        },
        {
            label: t('Compose Email'),
            onClick: () =>
                createEmailDraft({
                    subject: t('Untitled Email'),
                    template: template.template,
                    // parent_template: template.id,
                }),
            Icon: MailOutlineRounded,
        },
        {
            label: t('Move to'),
            onClick: () => setTemplateSelectOpen(true),
            Icon: DriveFileMoveOutlined,
        },
        {
            label: t('Delete'),
            Icon: DeleteOutlineRounded,
            sx: { color: 'error.main' },
            onClick: () => {
                dialog.open({
                    title: t('Delete Variant') + '?',
                    message: (
                        <Trans
                            i18nKey="prompts:content.deleteTemplateVariant"
                            values={{ title: template.title }}
                        />
                    ),
                    onConfirm: () => template.id,
                    confirmMutation: deleteMutation,
                    confirmButtonProps: { color: 'error' },
                    confirmLabel: t('Delete'),
                });
            },
        },
    ];

    const handleMoveSelect = async temp => {
        await updateTemplate.mutateAsync([template.id, { parent: temp.id }]);
        invalidateTemplates();
    };

    return (
        <>
            <TemplateSelect
                open={templateSelectOpen}
                onClose={() => setTemplateSelectOpen(false)}
                template={template}
                onSelect={handleMoveSelect}
                standalone
                actionText={t('Move')}
            />
            <QuickOptions size="small" options={quickOptions} />
        </>
    );
};

const TemplateCard = forwardRef(
    ({ template, onSelect, setSelectedTemplate }, ref) => {
        return (
            <Card
                ref={ref}
                variant="elevation"
                onClick={() => onSelect(template)}
                sx={{ cursor: 'pointer' }}
            >
                <Avatar
                    sx={{
                        width: '100%',
                        height: 196,
                        bgcolor: maxToneToLevel(template.background_color, 94),
                        borderRadius: 'inherit',
                    }}
                    src={template.thumbnail}
                >
                    <DashboardRounded sx={{ fontSize: 164 }} />
                </Avatar>
                <CardHeader
                    title={template.title}
                    titleTypographyProps={{
                        noWrap: true,
                        variant: 'titleLarge',
                    }}
                    sx={{
                        '& .MuiCardHeader-content': {
                            overflow: 'hidden',
                        },
                    }}
                    action={
                        <Box onClick={e => e.stopPropagation()}>
                            <VariantQuickActions
                                template={template}
                                onSelect={onSelect}
                                setSelectedTemplate={setSelectedTemplate}
                            />
                        </Box>
                    }
                />
            </Card>
        );
    },
);

const GridBox = ({ children }) => (
    <Box
        display="grid"
        gap={2}
        gridTemplateColumns="repeat(auto-fill, minmax(196px, 1fr))"
    >
        {children}
    </Box>
);

const TemplateList = ({
    templates,
    setSelectedTemplate,
    loadMore,
    loadingNext,
    isLoading,
    onSelect,
}) => {
    if (isLoading) {
        return <GridSkeleton />;
    }

    return (
        <InfiniteScroll
            ListComponent={GridBox}
            loadMore={loadMore}
            loading={loadingNext}
        >
            {templates.map(template => (
                <TemplateCard
                    key={template.id}
                    template={template}
                    onSelect={onSelect}
                    setSelectedTemplate={setSelectedTemplate}
                />
            ))}
        </InfiniteScroll>
    );
};

export default TemplateList;
